import { ProtoSpriteThree } from "protosprite-three";
import {
  BoxGeometry,
  Color,
  ColorRepresentation,
  Euler,
  InstancedMesh,
  Matrix4,
  MeshStandardMaterial,
  type Vector3
} from "three";

import {
  type BoardFacts,
  type Game,
  type Square,
  fileOf,
  intents,
  rankOf
} from "src/api";
import { kCenteralLoader } from "src/client/human/singletons/CentralLoader";

import {
  DARK_TILE_COLOR,
  LIGHT_TILE_COLOR,
  TILE_ROUGHNESS,
  TILE_THICKNESS
} from "../../../constants";
import { Tween } from "../../../render/animation/Tween";
import {
  easeInCubic,
  easeOutCubic
} from "../../../render/animation/easingFunctions";
import type { PickableWorldObject } from "../../../render/world/PickableWorldObject";
import { WorldObject } from "../../../render/world/WorldObject";
import {
  CAPTURABLE_TILE_COLOR,
  HIGHLIGHTED_TILE_COLOR,
  MOVE_ARROW_HEIGHT_ABOVE_TILE,
  MOVE_ARROW_SCALE,
  TILE_ANIMATION_SECONDS,
  TILE_ANIMATION_TRAVEL_DISTANCE
} from "../../gameConstants";
import { useSpawnSelection } from "../../spawnSelection";
import type { BoardUpdate } from "../updates/BoardUpdate";
import type { BoardWorldObject } from "../updates/BoardWorldObject";
import {
  rotationFromSquareToSquare,
  squareToWorld,
  worldToSquare
} from "../utilities/boardCoordinates";

// TMP constants move when locked in.
const kMovableGlowAmount = 0.5;
const kCapturableGlowAmount = 0.25;
const kHighlightTileColor = new Color(HIGHLIGHTED_TILE_COLOR);
const kCapturableTileColor = new Color(CAPTURABLE_TILE_COLOR);

type SquareModifierState = {
  glowAmount: number;
  glowColor?: Color;
  iconSprite?: ProtoSpriteThree;
};

export class BoardTiles
  extends WorldObject
  implements PickableWorldObject, BoardWorldObject
{
  private readonly geometry = new BoxGeometry(1, TILE_THICKNESS, 1);
  private readonly material = new MeshStandardMaterial({
    roughness: TILE_ROUGHNESS
  });
  private readonly spawningMaterial = new MeshStandardMaterial({
    roughness: TILE_ROUGHNESS,
    transparent: true
  });
  private readonly despawningMaterial = new MeshStandardMaterial({
    roughness: TILE_ROUGHNESS,
    transparent: true
  });
  private readonly tileIndexBySquare = new Map<Square, number>();
  private tiles: InstancedMesh | null = null;

  private groundSquares: Square[] = [];
  private spawningTiles: InstancedMesh | null = null;
  private despawningTiles: InstancedMesh | null = null;
  private spawnTween: Tween | null = null;
  private despawnTween: Tween | null = null;

  private squareToModifier = new Map<Square, SquareModifierState>();

  // Game is an acceptable thing to accept, but it's not a perfect solution.
  private game: Game;
  private sessionUnsubscribe?: () => void;

  constructor(game: Game) {
    super();
    this.game = game;
    this.initSubs();
  }

  private onSessionUpdate = () => {
    const session = this.game.session;
    if (!session) return;
    const view = session.view();

    // This is pretty naive but it works.
    const cleanupSquares = new Set(this.squareToModifier.keys());
    for (const square of cleanupSquares) {
      this.resetTileColor(square);
    }
    this.removeMoveArrows();
    this.squareToModifier.clear();

    // If it's not our turn don't show any highlights.
    if (!view.controlsTurn) {
      return;
    }

    for (const legalMoveSquare of view.highlights.legalMoves ?? []) {
      this.squareToModifier.set(legalMoveSquare, {
        glowAmount: kMovableGlowAmount,
        glowColor: kHighlightTileColor
      });
    }
    for (const captureableSquare of view.highlights.captures ?? []) {
      const squareModifiers: SquareModifierState = this.squareToModifier.get(
        captureableSquare
      ) ?? {
        glowAmount: kCapturableGlowAmount
      };
      squareModifiers.glowAmount = Math.max(
        kCapturableGlowAmount,
        squareModifiers.glowAmount
      );
      squareModifiers.glowColor = kCapturableTileColor;
      this.squareToModifier.set(captureableSquare, squareModifiers);
    }

    const selectedSquare = view.selected;
    if (selectedSquare !== undefined) {
      const captureSquares = new Set(view.highlights.captures);
      for (const legalMoveSquare of view.highlights.legalMoves ?? []) {
        if (captureSquares.has(legalMoveSquare)) continue;
        this.placeMoveArrow(legalMoveSquare, selectedSquare);
      }
    }

    this.applyHighlights();
  };

  private placeMoveArrow(square: Square, fromSquare: Square) {
    const sprite = kCenteralLoader.createIconSprite("arrow_right");

    sprite.mesh.position
      .copy(squareToWorld(square))
      .setY(MOVE_ARROW_HEIGHT_ABOVE_TILE);
    sprite.mesh.setRotationFromEuler(
      new Euler(-Math.PI / 2, 0, rotationFromSquareToSquare(fromSquare, square))
    );

    sprite.mesh.scale.multiplyScalar(MOVE_ARROW_SCALE);
    this.root.add(sprite.mesh);

    const modifier = this.squareToModifier.get(square);
    if (modifier) modifier.iconSprite = sprite;
  }

  private removeMoveArrows() {
    for (const modifier of this.squareToModifier.values()) {
      if (!modifier.iconSprite) continue;

      this.root.remove(modifier.iconSprite.mesh);
      modifier.iconSprite.dispose();
      modifier.iconSprite = undefined;
    }
  }

  // Also run after the tile mesh is rebuilt: a board update arrives after the
  // session update that lit the tiles, and a fresh mesh starts uncoloured.
  private applyHighlights() {
    for (const [square, modifier] of this.squareToModifier) {
      const tileColor = new Color(this.defaultTileColor(square)).lerp(
        modifier.glowColor ?? kHighlightTileColor,
        modifier.glowAmount
      );
      this.setTileColor(square, tileColor);
    }
  }

  initSubs() {
    this.sessionUnsubscribe = this.game.session?.subscribe(
      this.onSessionUpdate
    );
  }

  dispose() {
    this.sessionUnsubscribe?.();
    this.sessionUnsubscribe = undefined;
    this.removeMoveArrows();
    this.removeTiles();
    this.removeAnimatedTiles();
    this.geometry.dispose();
    this.material.dispose();
    this.spawningMaterial.dispose();
    this.despawningMaterial.dispose();
  }

  onBoardUpdate(boardUpdate: BoardUpdate) {
    const previousGroundSquares = this.groundSquares;
    this.groundSquares = this.findGroundSquares(boardUpdate.facts.squares);

    let addedSquares: Square[] = [];
    let removedSquares: Square[] = [];
    if (
      boardUpdate.delta.kind !== "load" &&
      boardUpdate.delta.kind !== "snap"
    ) {
      const previousSquareSet = new Set(previousGroundSquares);
      const newSquareSet = new Set(this.groundSquares);
      addedSquares = this.groundSquares.filter(
        (square) => !previousSquareSet.has(square)
      );
      removedSquares = previousGroundSquares.filter(
        (square) => !newSquareSet.has(square)
      );
    }

    const addedSquareSet = new Set(addedSquares);
    this.drawSettledTiles(
      this.groundSquares.filter((square) => !addedSquareSet.has(square))
    );

    if (addedSquares.length > 0) {
      this.spawningTiles = this.createTileMesh(
        addedSquares,
        this.spawningMaterial
      );
      this.root.add(this.spawningTiles);
      this.spawnTween = new Tween(TILE_ANIMATION_SECONDS, easeOutCubic);
    }

    if (removedSquares.length > 0) {
      this.despawningTiles = this.createTileMesh(
        removedSquares,
        this.despawningMaterial
      );
      this.root.add(this.despawningTiles);
      this.despawnTween = new Tween(TILE_ANIMATION_SECONDS, easeInCubic);
    }

    this.updateTileAnimations();
  }

  isBoardUpdateFinished() {
    return this.spawnTween === null && this.despawnTween === null;
  }

  step(deltaSeconds: number) {
    if (this.isBoardUpdateFinished()) return;

    this.spawnTween?.step(deltaSeconds);
    this.despawnTween?.step(deltaSeconds);
    this.updateTileAnimations();

    const spawnFinished = this.spawnTween?.isFinished ?? true;
    const despawnFinished = this.despawnTween?.isFinished ?? true;
    if (spawnFinished && despawnFinished) this.finishTileAnimations();
  }

  setTileColor(square: Square, color: ColorRepresentation) {
    const index = this.tileIndexBySquare.get(square);
    if (index === undefined || this.tiles === null) return;

    this.tiles.setColorAt(index, new Color(color));
    if (this.tiles.instanceColor) this.tiles.instanceColor.needsUpdate = true;
  }

  resetTileColor(square: Square) {
    this.setTileColor(square, this.defaultTileColor(square));
  }

  onPicked(hitPoint: Vector3) {
    const square = worldToSquare(hitPoint);

    if (this.placeSelectedPieceOrStructure(square)) return;

    let moveIsLegal = false;
    if (!this.game?.session) return;
    for (const legalSquare of this.game.session.view()?.highlights.legalMoves ??
      []) {
      if (square !== legalSquare) continue;
      moveIsLegal = true;
      break;
    }
    if (!moveIsLegal) return;
    this.game.session.tap(square);
  }

  // private highlightTile(square: Square) {
  //   if (!this.tileIndexBySquare.has(square)) return;

  //   if (this.highlightedSquare !== null)
  //     this.resetTileColor(this.highlightedSquare);
  //   this.setTileColor(square, HIGHLIGHTED_TILE_COLOR);
  //   this.highlightedSquare = square;
  // }

  // TODO: temp ignores the forge's cost and foundry position, and reads the
  //      forge menus own selection directly instead of the engine since the engine refuses
  //      an invalid piece getting selected without enough material or something
  private placeSelectedPieceOrStructure(square: Square): boolean {
    const { session, dev } = this.game;
    if (!session || !dev) return false;

    const pieceOrStructure = useSpawnSelection.getState().pieceOrStructure;
    if (!pieceOrStructure) return false;

    const squareFacts = this.game.board.at(square);
    if (squareFacts?.cell !== "board" || squareFacts.piece) return false;

    const side = session.view().side;
    if (pieceOrStructure.kind === "piece") {
      dev.dispatch(
        intents.dev.grow({
          pieces: [{ square, piece: pieceOrStructure.piece, side }]
        })
      );
    } else {
      dev.dispatch(
        intents.dev.grow({
          structures: [{ square, structure: pieceOrStructure.structure, side }]
        })
      );
    }

    return true;
  }

  private defaultTileColor(square: Square): string {
    return (fileOf(square) + rankOf(square)) % 2 !== 0
      ? LIGHT_TILE_COLOR
      : DARK_TILE_COLOR;
  }

  private findGroundSquares(squares: BoardFacts["squares"]): Square[] {
    const groundSquares: Square[] = [];
    for (const squareFacts of squares.values()) {
      // NOTE: "none" is no ground
      if (squareFacts.cell === "none") continue;
      groundSquares.push(squareFacts.square);
    }
    return groundSquares;
  }

  private createTileMesh(
    squares: Square[],
    material: MeshStandardMaterial
  ): InstancedMesh {
    const tiles = new InstancedMesh(this.geometry, material, squares.length);
    tiles.receiveShadow = true;

    const matrix = new Matrix4();
    squares.forEach((square, index) => {
      const position = squareToWorld(square).setY(-TILE_THICKNESS / 2);
      tiles.setMatrixAt(index, matrix.makeTranslation(position));
      tiles.setColorAt(index, new Color(this.defaultTileColor(square)));
    });
    return tiles;
  }

  private drawSettledTiles(squares: Square[]) {
    this.removeTiles();

    this.tiles = this.createTileMesh(squares, this.material);
    this.root.add(this.tiles);
    squares.forEach((square, index) =>
      this.tileIndexBySquare.set(square, index)
    );
    this.applyHighlights();
  }

  private updateTileAnimations() {
    if (this.spawningTiles !== null && this.spawnTween !== null) {
      this.placeAnimatedTiles(
        this.spawningTiles,
        this.spawningMaterial,
        this.spawnTween.progress
      );
    }
    if (this.despawningTiles !== null && this.despawnTween !== null) {
      this.placeAnimatedTiles(
        this.despawningTiles,
        this.despawningMaterial,
        1 - this.despawnTween.progress
      );
    }
  }

  private placeAnimatedTiles(
    tiles: InstancedMesh,
    material: MeshStandardMaterial,
    visibleAmount: number
  ) {
    tiles.position.y = (visibleAmount - 1) * TILE_ANIMATION_TRAVEL_DISTANCE;
    material.opacity = visibleAmount;
  }

  private finishTileAnimations() {
    this.removeAnimatedTiles();
    this.drawSettledTiles(this.groundSquares);
  }

  private removeTiles() {
    if (this.tiles !== null) {
      this.root.remove(this.tiles);
      this.tiles.dispose();
      this.tiles = null;
    }

    // TODO: testing only, remove later
    // this.highlightedSquare = null;

    this.tileIndexBySquare.clear();
  }

  private removeAnimatedTiles() {
    if (this.spawningTiles !== null) {
      this.root.remove(this.spawningTiles);
      this.spawningTiles.dispose();
      this.spawningTiles = null;
    }
    if (this.despawningTiles !== null) {
      this.root.remove(this.despawningTiles);
      this.despawningTiles.dispose();
      this.despawningTiles = null;
    }
    this.spawnTween = null;
    this.despawnTween = null;
  }
}
